import { Table } from '@dynamika-shared/ui/Table/Table';
import { DropdownOptionType, makeUsersColumns } from './UsersTable.columns';
import { FC, ReactNode, useState } from 'react';
import { OnSortColumnType, Pagination } from '@dynamika-shared/ui/Table/Table.types';
import { NoContent } from '@dynamika-shared/ui/NoContent/NoContent';
import { User } from '@dynamika-shared/types/users';
import { SubPageHeader } from '@components/SubPageHeader/SubPageHeader';
import { OrganizationType } from '@dynamika-shared/types/inventory';
import { UserListActionComponent } from './UserListActionComponent';

export type UsersListPageViewProps = {
  children: ReactNode;
  loading?: boolean;
  usersData: User[];
  totalItems: number;
  onSort: OnSortColumnType;
  sortState?: string;
  pagination: Pagination;
  institutions: OrganizationType[];
  refetchUsers: () => Promise<void>;
};

export const UsersListPageView: FC<UsersListPageViewProps> = ({
  usersData,
  totalItems,
  loading,
  onSort,
  sortState,
  pagination,
  children,
  institutions,
  refetchUsers,
}) => {
  const [currentUser, setCurrentUser] = useState<User>();
  const [actionMode, setActionMode] = useState<DropdownOptionType>();

  const usersColumns = makeUsersColumns({
    institutions,
    onUserClick: (user, actionType) => {
      setActionMode(actionType);
      setCurrentUser(user);
    },
  });

  return (
    <div data-testid='UsersPageView' className='relative h-[calc(100%-120px)]'>
      <SubPageHeader
        title='Users List'
        buttonText='User'
        showButton
        action={() => setActionMode(DropdownOptionType.create)}
      >
        {children}
      </SubPageHeader>
      <div className='mt-3 flex grow flex-col'>
        <Table
          columns={usersColumns}
          data={usersData}
          totalItems={totalItems}
          loading={loading}
          onSort={onSort}
          sortState={sortState}
          pagination={pagination}
          pageSizeOptions={[20, 50, 100]}
          isRowDisabled={row => !(row as User)?.enabled}
          noContentMessage={
            <NoContent
              action={() => setActionMode(DropdownOptionType.create)}
              actionText={'Create User'}
              iconName={'subjects'}
              title={
                <span className='text-1424 text-spanishGray'>
                  No{' '}
                  <span className='font-semibold text-chineseBlack'>Users</span>{' '}
                  here yet! <br /> Click the button to create an user!
                </span>
              }
            />
          }
        />
      </div>
      {actionMode && (
        <UserListActionComponent
          currentUser={currentUser}
          refetchUsers={refetchUsers}
          actionType={actionMode}
          onClose={() => {
            setActionMode(undefined);
            setCurrentUser(undefined);
          }}
        />
      )}
    </div>
  );
};
